// =====================================================================
// PeriodLockBanner.jsx - Banner UI pentru perioade blocate (Faza 3.4 D-007)
// =====================================================================
// Component reusabil afisat in:
//   - MesHeures.jsx (mod restrans: doar lunile acoperite de saptamana
//     curenta - pass `relevantPeriods` ca prop)
//   - ValidationTS.jsx (mod global: toate lunile blocate - fara prop)
//
// API: GET /api/period-locks/active (info-only, orice logged-in user).
// Auto-fetch la mount. Silent fail daca API picat (banner-ul e info,
// nu critic - blocajul real e enforcat backend).
//
// Stil: amber/yellow (match cu BackupAlertBanner din ValidationTS).
// =====================================================================

import { useState, useEffect } from 'react';
import { periodLocks as periodLocksApi } from '../services/api.js';

// Nume luna in franceza, forma lunga (vs MONTH_LABELS_FR din MesHeures
// care sunt abreviate "janv.", "fevr." etc.)
const MONTH_LABELS_FR_LONG = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/**
 * "2099-01" -> "janvier 2099"
 * Defensiv: returneaza input-ul daca format invalid.
 */
function formatPeriod(period) {
  if (!period || typeof period !== 'string') return period || '?';
  const [y, m] = period.split('-');
  const idx = parseInt(m, 10) - 1;
  if (Number.isNaN(idx) || idx < 0 || idx > 11) return period;
  return `${MONTH_LABELS_FR_LONG[idx]} ${y}`;
}

/**
 * ISO timestamp -> "DD/MM/YYYY" (fr-BE locale).
 */
function formatLockDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Banner D-007.
 *
 * @param {Object} props
 * @param {string[]} [props.relevantPeriods] - Array de "YYYY-MM" strings.
 *   Daca e prezent, filtreaza lock-urile la doar cele incluse (mod MesHeures).
 *   Daca lipseste, afiseaza TOATE lock-urile active (mod ValidationTS).
 */
export default function PeriodLockBanner({ relevantPeriods }) {
  const [locks, setLocks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await periodLocksApi.listActive();
        if (!cancelled) {
          setLocks(Array.isArray(data) ? data : []);
        }
      } catch (_err) {
        // Silent fail: banner-ul e info-only. Blocajul real ramane
        // enforcat backend, deci absenta banner-ului = degradare UX
        // acceptabila, nu siguranta compromisa.
        if (!cancelled) setLocks([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Loading: nu afisa nimic (evita flicker initial)
  if (!loaded) return null;

  // Filter dupa relevantPeriods (mod MesHeures) sau pass-through (mod ValidationTS)
  const filtered = Array.isArray(relevantPeriods)
    ? locks.filter((l) => relevantPeriods.includes(l.period))
    : locks;

  if (filtered.length === 0) return null;

  return (
    <div
      role="alert"
      style={{
        background: '#fef3c7',
        border: '1px solid #fcd34d',
        color: '#92400e',
        padding: '12px 16px',
        borderRadius: 6,
        marginBottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
        <span style={{ fontSize: 18 }} aria-hidden="true">🔒</span>
        <span>
          {filtered.length === 1
            ? 'Période verrouillée — modifications bloquées'
            : `${filtered.length} périodes verrouillées — modifications bloquées`}
        </span>
      </div>
      <ul style={{
        margin: '4px 0 0 0',
        paddingLeft: 28,
        fontSize: 13,
        lineHeight: 1.6,
      }}>
        {filtered.map((lock) => (
          <li key={lock.period}>
            <strong>{formatPeriod(lock.period)}</strong>
            {' verrouillée par '}
            <strong>{lock.lockedByName}</strong>
            {lock.lockedAt && ` le ${formatLockDate(lock.lockedAt)}`}
          </li>
        ))}
      </ul>
    </div>
  );
}
